import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, Pagination, Skeleton } from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import type {
  AdminRole,
  ReleaseScheduleCreateRequest,
  ReleaseScheduleListItem,
} from '@/lib/api-admin/types';
import {
  RELEASE_SORT_VALUES,
  useReleaseScheduleFiltersStore,
  type ReleaseSort,
} from '@/stores/useReleaseScheduleFiltersStore';
import { useAuthContext } from '@/providers/AuthProvider';
import { canWriteRelease } from '../lib/settlementActionRbac';
import { ReleaseFiltersBar } from '../components/ReleaseFiltersBar';
import { ReleaseSchedulesTable } from '../components/ReleaseSchedulesTable';
import { ReleaseCreateModal } from '../components/ReleaseCreateModal';
import { ReleaseCancelModal } from '../components/ReleaseCancelModal';
import { useReleaseSchedulesQuery } from '../hooks/useReleaseSchedulesQuery';
import {
  useCancelReleaseScheduleMutation,
  useCreateReleaseScheduleMutation,
} from '../hooks/useReleaseScheduleMutation';

const isReleaseSort = (s: string): s is ReleaseSort =>
  RELEASE_SORT_VALUES.includes(s as ReleaseSort);

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return err.message ?? err.code;
  if (err instanceof Error) return err.message;
  return null;
};

export const ReleaseSchedulesPage = () => {
  const { t } = useTranslation('settlement');
  const { currentAdmin } = useAuthContext();
  const roles = (currentAdmin?.roles ?? []) as ReadonlyArray<AdminRole>;
  const canWrite = canWriteRelease(roles);

  const keyword = useReleaseScheduleFiltersStore((s) => s.keyword);
  const vendorId = useReleaseScheduleFiltersStore((s) => s.vendorId);
  const artistId = useReleaseScheduleFiltersStore((s) => s.artistId);
  const status = useReleaseScheduleFiltersStore((s) => s.status);
  const source = useReleaseScheduleFiltersStore((s) => s.source);
  const from = useReleaseScheduleFiltersStore((s) => s.from);
  const to = useReleaseScheduleFiltersStore((s) => s.to);
  const page = useReleaseScheduleFiltersStore((s) => s.page);
  const size = useReleaseScheduleFiltersStore((s) => s.size);
  const sort = useReleaseScheduleFiltersStore((s) => s.sort);
  const setSort = useReleaseScheduleFiltersStore((s) => s.setSort);
  const setPage = useReleaseScheduleFiltersStore((s) => s.setPage);

  const query = useReleaseSchedulesQuery({
    keyword,
    vendorId,
    artistId,
    status,
    source,
    from,
    to,
    page,
    size,
    sort,
  });
  const createMutation = useCreateReleaseScheduleMutation();
  const cancelMutation = useCancelReleaseScheduleMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ReleaseScheduleListItem | null>(null);

  const items = query.data?.items ?? [];
  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;
  const createError = errorMessageOf(createMutation.error);
  const cancelError = errorMessageOf(cancelMutation.error);

  const sortButtons = (
    <div role="radiogroup" aria-label={t('settlement.release.filters.label')} className="flex flex-wrap gap-2">
      {([
        ['releaseAt,asc', t('settlement.release.filters.sortReleaseAsc')],
        ['releaseAt,desc', t('settlement.release.filters.sortReleaseDesc')],
        ['createdAt,desc', t('settlement.release.filters.sortCreatedDesc')],
      ] as const).map(([val, label]) => (
        <button
          key={val}
          type="button"
          role="radio"
          aria-checked={sort === val || (val === 'releaseAt,asc' && (!sort || sort === 'releaseAt,asc'))}
          onClick={() => setSort(val)}
          className="rounded-full border px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2"
          style={{
            background: sort === val ? 'var(--cta-primary)' : 'var(--surface-raised)',
            color: sort === val ? 'var(--cta-primary-on)' : 'var(--content-default)',
            borderColor: 'var(--content-muted)',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <PageShell
      title={
        <span className="flex flex-col gap-0.5">
          <span id="release-list-heading">{t('settlement.release.listTitle')}</span>
          <span className="text-xs font-normal text-[color:var(--content-muted)]">
            {t('settlement.release.listSubject')}
          </span>
        </span>
      }
      actions={
        canWrite ? (
          <Button
            type="button"
            variant="cta"
            onClick={() => setShowCreate(true)}
            data-testid="release-create-trigger"
          >
            {t('settlement.actions.createSchedule')}
          </Button>
        ) : undefined
      }
      aria-labelledby="release-list-heading"
    >
      <ReleaseFiltersBar />
      {sortButtons}

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          <p>{t('settlement.release.error')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('settlement.actions.retry')}
          </Button>
        </div>
      ) : (
        <ReleaseSchedulesTable
          items={items}
          canWrite={canWrite}
          onEdit={(_item) => {
            // Inline edit modal not wired; reserved for future iteration
          }}
          onCancel={(item) => setCancelTarget(item)}
        />
      )}

      <Pagination
        total={totalItems}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
      />

      <ReleaseCreateModal
        isOpen={showCreate}
        isSubmitting={createMutation.isPending}
        errorMessage={createError}
        onCancel={() => setShowCreate(false)}
        onConfirm={(body: ReleaseScheduleCreateRequest) =>
          createMutation.mutate(body, { onSuccess: () => setShowCreate(false) })
        }
      />

      <ReleaseCancelModal
        isOpen={Boolean(cancelTarget)}
        title={cancelTarget?.title ?? ''}
        isSubmitting={cancelMutation.isPending}
        errorMessage={cancelError}
        onCancel={() => setCancelTarget(null)}
        onConfirm={(values) => {
          if (!cancelTarget) return;
          cancelMutation.mutate(
            { scheduleId: cancelTarget.scheduleId, cancelReason: values.cancelReason },
            { onSuccess: () => setCancelTarget(null) },
          );
        }}
      />

      <span hidden data-testid="sort-helper">
        {isReleaseSort(sort) ? sort : ''}
      </span>
    </PageShell>
  );
};
